<!--
  NumberField — numeric entry for the field kit.

  Requirements it exists to satisfy (spec "UX rules that decide whether this
  beats paper"):
    - numeric keypad on a phone (inputmode / a text input, NOT type=number,
      so a fat glove cannot spin the value with the stepper and so a stray
      character does not silently blank the field);
    - large tap target, high contrast, one-handed;
    - out-of-range values are FLAGGED, never blocked — a real out-of-range
      reading is exactly what must be recorded;
    - fires on every keystroke so live computed values (W/C, wt/count,
      tolerance) update as the user types, and separately on blur so the
      screen can flush the section to the API.
-->
<script lang="ts">
  type Flag = 'ok' | 'warn' | 'bad' | null;

  interface Props {
    label: string;
    value: number | null;
    /** Unit suffix shown inside the control, e.g. 'kg', 'L', 'in', 's'. */
    unit?: string;
    /** Whole numbers only (counts). Switches the keypad to numeric. */
    integer?: boolean;
    /** Decimal places used when re-formatting on blur. null = leave as typed. */
    decimals?: number | null;
    placeholder?: string;
    /** Small helper line under the control, e.g. 'target 16-19'. */
    hint?: string;
    /** Advisory only. Colours the control; never prevents entry. */
    flag?: Flag;
    disabled?: boolean;
    /** Hide the label (row layouts that carry their own column header). */
    hideLabel?: boolean;
    /** Tighter geometry for table-style repeating rows. */
    compact?: boolean;
    id?: string;
    /** Fired on every keystroke — drives live computed values. */
    onValue?: (v: number | null) => void;
    /** Fired on blur — the screen flushes the section here. */
    onCommit?: (v: number | null) => void;
  }

  let {
    label,
    value,
    unit = '',
    integer = false,
    decimals = null,
    placeholder = '',
    hint = '',
    flag = null,
    disabled = false,
    hideLabel = false,
    compact = false,
    id = `num-${Math.random().toString(36).slice(2, 9)}`,
    onValue,
    onCommit
  }: Props = $props();

  function format(v: number | null): string {
    if (v === null || v === undefined || Number.isNaN(v)) return '';
    if (decimals !== null) return v.toFixed(decimals);
    return String(v);
  }

  /** Accepts '1.5', '1,5', '-2', '.5'. Anything else is null (not zero). */
  function parse(raw: string): number | null {
    const s = raw.trim().replace(',', '.');
    if (s === '' || s === '-' || s === '.' || s === '-.') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  let text = $state(format(value));
  let focused = $state(false);

  // Re-sync from the parent only while the user is not typing, so a server
  // round-trip can never yank characters out from under a thumb.
  $effect(() => {
    const v = value;
    if (!focused) text = format(v);
  });

  let malformed = $derived(text.trim() !== '' && parse(text) === null);

  function onInput(e: Event) {
    text = (e.currentTarget as HTMLInputElement).value;
    onValue?.(parse(text));
  }

  function onBlur() {
    focused = false;
    const v = parse(text);
    text = format(v);
    onCommit?.(v);
  }
</script>

<div
  class="nf"
  class:compact
  class:flag-ok={flag === 'ok'}
  class:flag-warn={flag === 'warn'}
  class:flag-bad={flag === 'bad'}
  class:malformed
>
  <label for={id} class:sr-only={hideLabel}>{label}</label>
  <div class="control">
    <input
      {id}
      type="text"
      inputmode={integer ? 'numeric' : 'decimal'}
      enterkeyhint="next"
      autocomplete="off"
      autocorrect="off"
      spellcheck="false"
      {placeholder}
      {disabled}
      value={text}
      oninput={onInput}
      onfocus={() => (focused = true)}
      onblur={onBlur}
      aria-invalid={malformed}
      aria-describedby={hint ? `${id}-hint` : undefined}
    />
    {#if unit}<span class="unit" aria-hidden="true">{unit}</span>{/if}
  </div>
  {#if hint}<p class="hint" id="{id}-hint">{hint}</p>{/if}
</div>

<style>
  .nf {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  label {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.2;
    color: var(--fg-muted, #3f4750);
    letter-spacing: 0.01em;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .control {
    display: flex;
    align-items: stretch;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    overflow: hidden;
  }

  input {
    flex: 1 1 auto;
    min-width: 0;
    /* 16px minimum: anything smaller makes iOS Safari zoom on focus. */
    font-size: 1.125rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
    padding: 0.75rem 0.625rem;
    min-height: 3rem;
    border: 0;
    background: transparent;
    color: var(--fg, #10151b);
    text-align: right;
  }

  .compact input {
    font-size: 1rem;
    padding: 0.5rem;
    min-height: 2.75rem;
  }

  input:focus {
    outline: none;
  }

  .control:focus-within {
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  input:disabled {
    color: var(--fg-muted, #3f4750);
    -webkit-text-fill-color: var(--fg-muted, #3f4750);
    opacity: 1;
    background: var(--surface-disabled, #eef1f4);
  }

  .unit {
    display: flex;
    align-items: center;
    padding: 0 0.625rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--fg-muted, #3f4750);
    background: var(--surface-muted, #eef1f4);
    border-left: 2px solid var(--border, #9aa3ad);
  }

  .hint {
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.25;
    color: var(--fg-muted, #3f4750);
  }

  /* Advisory colouring. Never blocks entry. */
  .flag-ok .control {
    border-color: var(--good, #1a7f45);
  }
  .flag-warn .control {
    border-color: var(--warn, #a35a00);
    background: var(--warn-bg, #fff6e6);
  }
  .flag-bad .control {
    border-color: var(--bad, #b3261e);
    background: var(--bad-bg, #fdecea);
  }
  .malformed .control {
    border-color: var(--bad, #b3261e);
  }
</style>
